import React from "react";
//#region redux
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { CommonEnums, DialogEnums } from "models/enums";
import { CustomerConstants, IconDefinitionConstants, RouteConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  IconTitle,
  GenericCommentsView,
  PageLayouts,
  RouteBreadCrumb,
  CreditRating,
  StyledButton,
  useMountedState,
  ViewPanel,
  ProfileImage,
  MasonryPanel,
  WidgetArea,
  Icon,
} from "components/common";
import ReferencePersonsList from "./contactList";
import ReferencePersonFormContainer from "./forms/referencePersonFormContainer";
import CustomerEconomyDetails from "./economyDetails";
import CustomerDetails from "./generalDetails";
import CustomerLatestInvoiceList from "./invoiceList";
import CustomerSimpleTodoCard from "./simpleTodoCard";
import useRemoveCustomerAction from "./useRemoveCustomerAction";
import { useCreateInvoice, useInvoicePermissions } from "components/invoice";
import { useCustomerService } from "serviceHooks";
import { Avatar, Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import AvatarSmall from "images/avatar-person-small.png";

const CustomerProfile: React.FC = () => {
  const customerService = useCustomerService();
  const { getMounted } = useMountedState();
  const urlParams = useParams<EntityIdParams>();
  const id = Number.parseInt(urlParams.id ?? "0");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [customer, setCustomer] = React.useState<ApiModel.Customer>();
  const onCreateInvoice = useCreateInvoice({ customer: customer });
  const { canCreateInvoices, canUseInvoices, hasChartsPermissions } = useInvoicePermissions();

  const { removeCustomerMoreAction } = useRemoveCustomerAction(customer?.id ?? 0, customer?.canRemove ?? false);

  const getCustomer = () => {
    customerService
      .getCustomer(id)
      .then((result) => {
        if (getMounted()) {
          setCustomer(result);
        }
      })
      .catch((e) => {
        console.warn("error");
      });
  };

  React.useEffect(() => {
    getCustomer();
  }, [id]);

  const goToSettings = (key?: string, subMenuKey?: string) => {
    const queryParams = key
      ? new URLSearchParams({ [RouteConstants.QueryParamStrings.Section]: key }).toString()
      : undefined;

    let baseUrl = `/Customer/${id}/Settings`;
    if (queryParams) {
      baseUrl += `?${queryParams}`;
    }

    if (subMenuKey) {
      baseUrl += `#${subMenuKey}`;
    }

    navigate(UrlHelper.formatPath(baseUrl), {
      state: { background: location },
    });
  };

  const onReferencePersonClick = (referencePerson: ApiModel.Reference) => {
    dispatch(
      displayDialog({
        isOpen: false,
        title: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)} ${
          referencePerson.name ?? ""
        }`,
        titleIcon: IconDefinitionConstants.WBusinessCard,
        dialogType: DialogEnums.DialogType.Content,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
            successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Saved),
          },
        ],
        children: (
          <ReferencePersonFormContainer
            customerId={id}
            id={referencePerson.id}
            name={referencePerson.name ?? ""}
            email={referencePerson.emailAddress}
            phoneNumber={referencePerson.phone}
            referenceId={referencePerson.referenceIdentifier}
            isDefault={referencePerson.isDefault}
            handleSubmit={async () => {
              getCustomer();
            }}
          />
        ),
        maxWidth: "sm",
      })
    );
  };

  const topRightButtons = canCreateInvoices ? (
    <DashboardButtonGroup
      buttons={[
        <StyledButton size="sm" onClick={onCreateInvoice}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreateInvoice)}
        </StyledButton>,
        ACTION_SEPARATOR,
        <StyledButton
          size="sm"
          onClick={goToSettings.bind(null, undefined, undefined)}
          className={cssExports.iconButton}
        >
          <Icon icon={IconDefinitionConstants.WSettings} />
        </StyledButton>,
      ]}
      moreActions={[removeCustomerMoreAction]}
    />
  ) : undefined;

  return (
    <PageLayouts.DashboardLayout
      entityKey={id}
      hasPermissions={canUseInvoices}
      title={
        customer ? (
          <IconTitle
            title={customer?.name ?? ""}
            subtitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Customer)}
            extraTags={
              customer.inactive
                ? [
                    {
                      tags: [LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InActive)],
                    },
                  ]
                : undefined
            }
            icon={
              customer.inactive ? (
                <Avatar className={cssExports.profileAvatar} alt={""} src={AvatarSmall} />
              ) : (
                <ProfileImage id={0} name={customer?.name ?? ""} />
              )
            }
            defaultTagColor={customer.inactive ? cssExports.grayTag : undefined}
            defaultTagSurface="background"
          />
        ) : (
          ""
        )
      }
      topRightButtons={topRightButtons}
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={customer?.name ?? ""} />}
    >
      <MasonryPanel>
        <CustomerDetails customer={customer} />
        {hasChartsPermissions() ? <CustomerSimpleTodoCard customerId={id} /> : <></>}

        {hasChartsPermissions() ? (
          <WidgetArea>
            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.CreditRating)}
              appearance="sm"
              gridSize="md"
            >
              <CreditRating orgNr={customer?.orgNumber} orgType={CommonEnums.OrganizationTypes.Customer} />
            </ViewPanel>
            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Economy)}
              topRightContent={
                <StyledButton
                  onClick={goToSettings.bind(null, "invoiceSettings", CustomerConstants.SettingsNavKeys.Payment)}
                  size="xs"
                >
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)}
                </StyledButton>
              }
              appearance="sm"
              gridSize="md"
            >
              <CustomerEconomyDetails customer={customer} />
            </ViewPanel>
          </WidgetArea>
        ) : (
          <></>
        )}
        <WidgetArea>
          <ViewPanel
            icon={IconDefinitionConstants.WUser}
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReferencePersons)}
            topRightContent={
              <StyledButton
                onClick={goToSettings.bind(null, "customerSettings", CustomerConstants.SettingsNavKeys.Reference)}
                size="xs"
              >
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)}
              </StyledButton>
            }
          >
            <ReferencePersonsList contacts={customer?.referencePersonsList} onRowClick={onReferencePersonClick} />
          </ViewPanel>
        </WidgetArea>
      </MasonryPanel>

      <WidgetArea>
        <CustomerLatestInvoiceList customerId={id} />
      </WidgetArea>

      <Grid item xs={12}>
        <GenericCommentsView parentId={id} type={ApiModel.GenericCommentType.InvoiceCustomer} />
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default CustomerProfile;
